import { ProNavigation } from "@/components/ProNavigation";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

export default function ClayLibrary() {
  const navigate = useNavigate();

  const clayTypes = [
    { color: "❤️ Rouge", composition: "Fer très oxydé", usage: "Peaux matures / sensibles" },
    { color: "🟡 Jaune", composition: "Fer modérément oxydé", usage: "Peaux normales à mixtes" },
    { color: "💚 Verte", composition: "Fer peu oxydé", usage: "Peaux grasses, purifiante" },
    { color: "💙 Bleue", composition: "Fer faiblement oxydé", usage: "Revitalisante" },
    { color: "⚪ Blanche", composition: "Sans fer", usage: "Ultra douce, peaux sensibles / bébé" },
    { color: "🌸 Rose", composition: "Mélange blanche + rouge", usage: "Peaux sensibles, effet \"peau nue\"" },
    { color: "🪶 Rhassoul", composition: "Origine Atlas (Maroc)", usage: "Lavant corps & cheveux, moussant" },
  ];

  return (
    <div className="min-h-screen bg-gradient-hero py-8 px-4">
      <ProNavigation />
      
      <div className="max-w-6xl mx-auto">
        <div className="mb-6 flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:justify-between">
          <Button variant="outline" size="sm" onClick={() => navigate('/treatments-library')}>
            <ArrowLeft className="w-4 h-4 mr-1" />
            <span className="hidden sm:inline">Bibliothèque de Traitements</span>
            <span className="sm:hidden">Retour</span>
          </Button>
          <h1 className="text-xl sm:text-3xl font-bold text-primary">🌍 Argile</h1>
          <div className="hidden sm:block w-[120px]" />
        </div>

        <Tabs defaultValue="introduction" className="space-y-6">
          <TabsList className="flex w-full overflow-x-auto gap-1 h-auto flex-wrap sm:flex-nowrap">
            <TabsTrigger value="introduction" className="text-xs sm:text-sm whitespace-nowrap flex-shrink-0">Introduction</TabsTrigger>
            <TabsTrigger value="types" className="text-xs sm:text-sm whitespace-nowrap flex-shrink-0">Types</TabsTrigger>
            <TabsTrigger value="formes" className="text-xs sm:text-sm whitespace-nowrap flex-shrink-0">Formes</TabsTrigger>
            <TabsTrigger value="usage-interne" className="text-xs sm:text-sm whitespace-nowrap flex-shrink-0">Interne</TabsTrigger>
            <TabsTrigger value="usage-externe" className="text-xs sm:text-sm whitespace-nowrap flex-shrink-0">Externe</TabsTrigger>
          </TabsList>

          <TabsContent value="introduction">
            <Card>
              <CardHeader>
                <CardTitle>🌍 Qu'est-ce que l'argile ?</CardTitle>
                <CardDescription>Un remède naturel millénaire</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-3">
                  <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
                    <span className="text-xl">🟤</span>
                    <p>Roche sédimentaire issue de la décomposition de minéraux</p>
                  </div>
                  <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
                    <span className="text-xl">🧪</span>
                    <p>Riche en oligo-éléments et minéraux</p>
                  </div>
                  <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
                    <span className="text-xl">📜</span>
                    <p>Utilisée depuis l'Antiquité (Égypte)</p>
                  </div>
                  <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
                    <span className="text-xl">🛡️</span>
                    <p>Vertus antimicrobiennes, protectrices, assainissantes</p>
                  </div>
                </div>

                <Card className="mt-6 border-primary/20">
                  <CardHeader>
                    <CardTitle className="text-lg">✅ En résumé</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="mb-4">🌱 L'argile est un remède naturel polyvalent :</p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <Badge variant="outline" className="justify-center py-2">🧽 Elle purifie</Badge>
                      <Badge variant="outline" className="justify-center py-2">💪 Elle reminéralise</Badge>
                      <Badge variant="outline" className="justify-center py-2">⚖️ Elle rééquilibre</Badge>
                      <Badge variant="outline" className="justify-center py-2">🩹 Elle apaise et guérit</Badge>
                    </div>
                    <p className="mt-4 text-center font-semibold text-primary">
                      🎒 Un indispensable de la trousse naturopathique !
                    </p>
                  </CardContent>
                </Card>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="types">
            <Card>
              <CardHeader>
                <CardTitle>🎨 Types d'argile</CardTitle>
                <CardDescription>Selon teneur et oxydation du fer</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Couleur</TableHead>
                      <TableHead>Composition</TableHead>
                      <TableHead>Usage conseillé</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {clayTypes.map((clay, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="font-medium">{clay.color}</TableCell>
                        <TableCell>{clay.composition}</TableCell>
                        <TableCell>{clay.usage}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="formes">
            <Card>
              <CardHeader>
                <CardTitle>🧴 Formes disponibles</CardTitle>
                <CardDescription>Les différentes préparations d'argile</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4">
                  <Card className="border-l-4 border-l-primary">
                    <CardContent className="pt-4">
                      <h4 className="font-semibold mb-2">🧂 Poudre</h4>
                      <ul className="space-y-2 text-muted-foreground">
                        <li>• <strong>Ultra ventilée</strong> → usage interne 🍵</li>
                        <li>• <strong>Surfine / ventilée</strong> → usage externe 🧖‍♀️</li>
                      </ul>
                    </CardContent>
                  </Card>

                  <Card className="border-l-4 border-l-secondary">
                    <CardContent className="pt-4">
                      <h4 className="font-semibold mb-2">🧱 Morceaux</h4>
                      <p className="text-muted-foreground">À réhydrater (pâte à préparer)</p>
                    </CardContent>
                  </Card>

                  <Card className="border-l-4 border-l-accent">
                    <CardContent className="pt-4">
                      <h4 className="font-semibold mb-2">🧴 Pâte prête à l'emploi</h4>
                      <p className="text-muted-foreground">Pratique, nomade, usage rapide</p>
                    </CardContent>
                  </Card>

                  <Card className="border-l-4 border-l-muted-foreground">
                    <CardContent className="pt-4">
                      <h4 className="font-semibold mb-2">💊 Gélules / comprimés</h4>
                      <p className="text-muted-foreground">Troubles digestifs, transport facile</p>
                    </CardContent>
                  </Card>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="usage-interne">
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>🧃 Utilisation interne</CardTitle>
                  <CardDescription>Mode d'emploi et indications</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="p-4 rounded-lg bg-primary/10 border border-primary/20">
                    <p className="font-semibold mb-3">🥄 1 c. à café d'argile dans un verre d'eau le soir :</p>
                    <ul className="space-y-2">
                      <li><strong>Eau d'argile</strong> (sans remuer le matin) : reminéralisante</li>
                      <li><strong>Eau argileuse</strong> (remuer avant de boire) : complète, curative</li>
                    </ul>
                  </div>

                  <div className="flex items-center gap-2 text-muted-foreground">
                    <span>⏳</span>
                    <p><strong>Cure :</strong> 3 semaines + pause 8 jours</p>
                  </div>

                  <div>
                    <p className="font-semibold mb-2">🎯 Indications :</p>
                    <div className="flex flex-wrap gap-2">
                      <Badge variant="outline">Acidité</Badge>
                      <Badge variant="outline">Diarrhée</Badge>
                      <Badge variant="outline">Infection intestinale</Badge>
                      <Badge variant="outline">Ostéoporose</Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-destructive/50">
                <CardHeader>
                  <CardTitle className="text-destructive">⚠️ Contre-indications</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="flex items-center gap-2 p-2 rounded bg-destructive/10">
                      <span>🚫</span>
                      <span>Femme enceinte / allaitante</span>
                    </div>
                    <div className="flex items-center gap-2 p-2 rounded bg-destructive/10">
                      <span>🚫</span>
                      <span>Hernie / occlusion intestinale / constipation</span>
                    </div>
                    <div className="flex items-center gap-2 p-2 rounded bg-destructive/10">
                      <span>🚫</span>
                      <span>Médicaments, contraceptifs oraux</span>
                    </div>
                    <div className="flex items-center gap-2 p-2 rounded bg-destructive/10">
                      <span>🚫</span>
                      <span>Hypertension / huile de paraffine / régime gras</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="usage-externe">
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>🧴 Utilisation externe : Cataplasme</CardTitle>
                  <CardDescription>Mode d'emploi</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid gap-3">
                    <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
                      <span>👣</span>
                      <p>Mélanger eau + argile (pâte épaisse)</p>
                    </div>
                    <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
                      <span>🖐️</span>
                      <p>Appliquer sur zone, débordant de 3 cm</p>
                    </div>
                    <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
                      <span>📏</span>
                      <p>Épaisseur : 2 cm environ</p>
                    </div>
                    <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
                      <span>🕒</span>
                      <p>Durée : 1h à toute une nuit</p>
                    </div>
                  </div>

                  <div>
                    <p className="font-semibold mb-2">🎯 Indications :</p>
                    <div className="flex flex-wrap gap-2">
                      <Badge variant="outline">Douleurs</Badge>
                      <Badge variant="outline">Plaies</Badge>
                      <Badge variant="outline">Entorses</Badge>
                      <Badge variant="outline">Rhumatismes</Badge>
                      <Badge variant="outline">Abcès</Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-destructive/50">
                <CardHeader>
                  <CardTitle className="text-destructive">⚠️ Ne pas utiliser</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 p-2 rounded bg-destructive/10">
                      <span>🚫</span>
                      <span>Bas-ventre en cas de règles ou stérilet</span>
                    </div>
                    <div className="flex items-center gap-2 p-2 rounded bg-destructive/10">
                      <span>🚫</span>
                      <span>Après repas sur ventre / foie / estomac</span>
                    </div>
                    <div className="flex items-center gap-2 p-2 rounded bg-destructive/10">
                      <span>🚫</span>
                      <span>Sur agrafes, points, prothèses, vis...</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-amber-500/50">
                <CardHeader>
                  <CardTitle className="text-amber-600">⚠️ Conseils importants</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 p-2 rounded bg-amber-500/10">
                      <span>❌</span>
                      <span>Pas de métal (cuillère ou récipient)</span>
                    </div>
                    <div className="flex items-center gap-2 p-2 rounded bg-amber-500/10">
                      <span>💧</span>
                      <span>Utiliser de l'eau de source</span>
                    </div>
                    <div className="flex items-center gap-2 p-2 rounded bg-amber-500/10">
                      <span>🌿</span>
                      <span>Ne jamais interrompre un traitement brutalement</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
